<template>
  <Dialog
    :open="isOpen"
    @update:open="(open) => emit('update:isOpen', open)"
  >
    <DialogContent class="max-w-3xl p-0">
      <DialogHeader class="px-8 py-6 pb-4">
        <DialogTitle class="text-xl font-semibold text-gray-900">
          {{ t('addDocuments') }}
        </DialogTitle>
        <DialogDescription class="mt-1 text-gray-600">
          {{ t('addDocumentsDescription') }}
        </DialogDescription>
      </DialogHeader>

      <div class="px-8">
        <div class="max-h-60 overflow-y-auto">
          <div
            v-if="editableDocuments.length === 0"
            class="py-8 text-center text-gray-500"
          >
            <FileText class="mx-auto mb-2 h-8 w-8 text-gray-300" />
            <p>{{ t('noExistingDocuments') }}</p>
          </div>
          <div
            v-for="doc in editableDocuments"
            :key="doc.uuid"
            class="flex items-center gap-4 rounded-lg p-4 transition-colors hover:bg-gray-50"
          >
            <input
              v-model="selectedExistingDocuments"
              type="checkbox"
              :value="doc.uuid"
              class="h-5 w-5 rounded border-gray-300 text-purple-600 focus:ring-2 focus:ring-purple-500
                focus:ring-offset-0"
            />
            <div class="flex flex-1 items-center gap-3">
              <div
                class="flex h-16 w-12 items-center justify-center overflow-hidden rounded border border-gray-200
                  bg-gray-100"
              >
                <img
                  v-if="doc.images && doc.images.length > 0"
                  :src="doc.images[0].url"
                  :alt="doc.filename"
                  class="h-full w-full object-cover"
                />
                <FileText
                  v-else
                  class="h-6 w-6 text-gray-500"
                />
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="truncate font-medium text-gray-900">
                    {{ doc.filename?.replace('.pdf', '') || t('untitled') }}
                  </span>
                </div>
                <p class="text-sm text-gray-500">{{ doc.page_count || 1 }} {{ t('pages') }}</p>
              </div>
            </div>
            <span class="whitespace-nowrap text-sm text-gray-400">
              {{ formatDate(doc.created_at) }}
            </span>
          </div>
        </div>

        <DocumentUpload
          :case-data="{ uuid: caseId } as any"
          :max-size="100"
          @files-selected="onFilesSelected"
          @file-uploaded="onFileUploaded"
        />
      </div>

      <div
        class="flex justify-end gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-8 py-4"
      >
        <Button
          variant="secondary"
          class="border-gray-300 bg-white px-4 py-2 text-gray-700 hover:bg-gray-50"
          @click="closeDialog"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          :disabled="selectedExistingDocuments.length === 0 && uploadedDocumentUuids.length === 0"
          class="bg-purple-600 px-4 py-2 font-medium text-white hover:bg-purple-700"
          @click="confirmAddDocuments"
        >
          {{ t('addSelected') }}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
  import DocumentUpload from '@/components/app/cases/documents/DocumentUpload.vue'
  import { Button } from '@/components/ui/button'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { useToast } from '@/components/ui/toast'
  import { isDocumentReadOnly } from '@/composables/useDocumentProcessing'
  import { useTranslation } from '@/composables/useTranslation'
  import type { Document, ImageproxyImagePreset } from '@/types/generated'
  import { DocumentsService } from '@/types/generated'
  import { FileText } from 'lucide-vue-next'
  import { computed, ref, watch } from 'vue'

  export interface PageItem {
    uuid: string
    filename: string
    images: any[]
    thumbnails: any[]
    pageNumber: number
    actualPageNumber: number
    documentUuid: string
    url: string
  }

  interface Props {
    isOpen: boolean
    availableDocuments: Document[]
    caseId: string
    allCaseDocuments: Document[]
  }

  interface Emits {
    (e: 'update:isOpen', value: boolean): void
    (
      e: 'confirm',
      data: { selectedDocuments: Document[]; uploadedFiles: any[]; pages: PageItem[] }
    ): void
    (e: 'fileUploaded'): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()
  const { toast } = useToast()

  const { t } = useTranslation({
    en: {
      addDocuments: 'Add documents',
      addDocumentsDescription:
        'Select existing documents or upload new ones to add to this document group.',
      noExistingDocuments: 'No existing documents available',
      untitled: 'Untitled',
      pages: 'pages',
      cancel: 'Cancel',
      addSelected: 'Add selected',
      error: 'Error',
      selectDocumentsOrUpload: 'Please select existing documents or upload new files',
      fileUploaded: 'File Uploaded',
      fileUploadedSuccess: 'File uploaded successfully'
    },
    de: {
      addDocuments: 'Dokumente hinzufügen',
      addDocumentsDescription:
        'Wählen Sie vorhandene Dokumente aus oder laden Sie neue hoch, um sie zu dieser Dokumentengruppe hinzuzufügen.',
      noExistingDocuments: 'Keine vorhandenen Dokumente verfügbar',
      untitled: 'Ohne Titel',
      pages: 'Seiten',
      cancel: 'Abbrechen',
      addSelected: 'Ausgewählte hinzufügen',
      error: 'Fehler',
      selectDocumentsOrUpload:
        'Bitte wählen Sie vorhandene Dokumente aus oder laden Sie neue Dateien hoch',
      fileUploaded: 'Datei hochgeladen',
      fileUploadedSuccess: 'Datei erfolgreich hochgeladen'
    }
  })

  // Internal state
  const selectedExistingDocuments = ref<string[]>([])
  const uploadedFiles = ref<any[]>([])
  const uploadedDocumentUuids = ref<string[]>([])

  // Filter out read-only documents from available documents
  const editableDocuments = computed(() => {
    return props.availableDocuments.filter((doc) => !isDocumentReadOnly(doc))
  })

  // Watch for uploaded documents and auto-select them if they appear in editableDocuments
  watch(
    () => [uploadedDocumentUuids.value, editableDocuments.value],
    () => {
      uploadedDocumentUuids.value.forEach((uuid) => {
        const docExists = editableDocuments.value.some((doc) => doc.uuid === uuid)
        if (docExists && !selectedExistingDocuments.value.includes(uuid)) {
          selectedExistingDocuments.value.push(uuid)
        }
      })
    },
    { deep: true }
  )

  // Methods
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const onFilesSelected = (files: any[]) => {
    uploadedFiles.value = files
  }

  const onFileUploaded = async (documentUuid: string) => {
    // Store the uploaded document UUID for later processing
    uploadedDocumentUuids.value.push(documentUuid)

    emit('fileUploaded')

    toast({
      title: t('fileUploaded'),
      description: t('fileUploadedSuccess'),
      variant: 'default'
    })
  }

  const closeDialog = () => {
    emit('update:isOpen', false)
    // Reset state when closing
    selectedExistingDocuments.value = []
    uploadedFiles.value = []
    uploadedDocumentUuids.value = []
  }

  const confirmAddDocuments = async () => {
    if (selectedExistingDocuments.value.length === 0 && uploadedDocumentUuids.value.length === 0) {
      toast({
        title: t('error'),
        description: t('selectDocumentsOrUpload'),
        variant: 'destructive'
      })
      return
    }

    const selectedDocuments: Document[] = []
    const allPages: PageItem[] = []

    for (const uuid of selectedExistingDocuments.value) {
      const doc = props.allCaseDocuments.find((d) => d.uuid === uuid)
      if (doc) {
        let thumbnails: ImageproxyImagePreset[] = []
        try {
          const thumbnailResponse = await DocumentsService.documentsThumbnailsRetrieve({
            uuid: doc.uuid
          })
          thumbnails = thumbnailResponse.thumbnails || []
        } catch (error) {
          console.error('Failed to fetch thumbnails:', error)
          thumbnails = doc.images || []
        }

        selectedDocuments.push(doc)
        const pageCount = doc.page_count || 1

        for (let i = 0; i < pageCount; i++) {
          const page: PageItem = {
            uuid: `${doc.uuid}-page-${i + 1}`,
            filename: doc.filename || 'Untitled',
            images: doc.images || [],
            thumbnails: thumbnails,
            pageNumber: i + 1,
            actualPageNumber: i + 1,
            documentUuid: doc.uuid,
            url: doc.url || ''
          }
          allPages.push(page)
        }
      }
    }

    for (const documentUuid of uploadedDocumentUuids.value) {
      // Skip if already processed in selectedExistingDocuments
      if (selectedExistingDocuments.value.includes(documentUuid)) {
        continue
      }

      try {
        let doc = props.allCaseDocuments.find((d) => d.uuid === documentUuid)

        if (!doc) {
          doc = await DocumentsService.documentsRetrieve({ uuid: documentUuid })
        }

        if (doc) {
          let thumbnails: ImageproxyImagePreset[] = []
          try {
            const thumbnailResponse = await DocumentsService.documentsThumbnailsRetrieve({
              uuid: doc.uuid
            })
            thumbnails = thumbnailResponse.thumbnails || []
          } catch (error) {
            console.error('Failed to fetch thumbnails:', error)
            thumbnails = doc.images || []
          }

          selectedDocuments.push(doc)
          const pageCount = doc.page_count || 1

          for (let i = 0; i < pageCount; i++) {
            const page: PageItem = {
              uuid: `${doc.uuid}-page-${i + 1}`,
              filename: doc.filename || 'Untitled',
              images: doc.images || [],
              thumbnails: thumbnails,
              pageNumber: i + 1,
              actualPageNumber: i + 1,
              documentUuid: doc.uuid,
              url: doc.url || ''
            }
            allPages.push(page)
          }
        }
      } catch (error) {
        console.error(`Failed to fetch uploaded document ${documentUuid}:`, error)
      }
    }

    emit('confirm', {
      selectedDocuments,
      uploadedFiles: uploadedFiles.value,
      pages: allPages
    })

    closeDialog()
  }
</script>
